import { PaginatedResponse } from './api';

export interface TurbineWorkspaceMapping {
  mapping_id: string;
  turbine_id: string;
  turbine_name: string;
  site_id: string;
  site_name: string;
  workspace_id: string;
  workspace_name: string;
  component_id?: string;
  component_name?: string;
  major_component_id?: string;
  major_component_name?: string;
  created_at: string;
  created_by: string;
}

export interface NewTurbineWorkspaceMapping {
  turbine_id: string;
  workspace_id: string;
  component_name?: string;
  major_component_name?: string;
}

export interface UpdateTurbineWorkspaceMapping {
  mapping_id: string;
  workspace_id: string;
  component_name?: string;
  major_component_name?: string;
}

export interface BulkTurbineWorkspaceMapping {
  turbine_ids: string[];
  workspace_id: string;
  component_name?: string;
  major_component_name?: string;
}

export interface BulkSiteTurbineWorkspaceMapping {
  site_id: string;
  workspace_id: string;
  component_name?: string;
  major_component_name?: string;
  turbine_filter?: {
    turbine_ids?: string[];
    exclude_turbine_ids?: string[];
  };
}

export interface TurbineWorkspaceMappingFilter {
  site_id?: string;
  workspace_id?: string;
  component_name?: string;
  major_component_name?: string;
  turbine_id?: string;
  has_mapping?: boolean;
}

export interface TurbineWorkspaceMappingsResponse extends PaginatedResponse<TurbineWorkspaceMapping> {
  mappings: TurbineWorkspaceMapping[];
}

export interface TurbineWithMapping {
  turbine_id: string;
  turbine_name: string;
  site_id: string;
  site_name: string;
  cod_date: string;
  model_name: string;
  manufacturer_name: string;
  mapping?: TurbineWorkspaceMapping;
}

export interface TurbineWithMappingsResponse extends PaginatedResponse<TurbineWithMapping> {
  turbines: TurbineWithMapping[];
}

export interface MappingStats {
  total_turbines: number;
  mapped_turbines: number;
  unmapped_turbines: number;
  total_workspaces_used: number;
  mappings_by_site: Record<string, {
    site_name: string;
    total_turbines: number;
    mapped_turbines: number;
  }>;
}

export interface CSVMappingRow {
  site_name: string;
  turbine_name: string;
  workspace_name: string;
  component_name?: string;
  major_component_name?: string;
  owner: string;
  line_num: number;
}

export interface CSVImportResult {
  successful: CSVMappingRow[];
  failed: {
    row: CSVMappingRow;
    errors: string[];
    lineNum: number;
  }[];
  summary: {
    total_rows: number;
    successful_count: number;
    failed_count: number;
  };
}

export interface CreateConfigFromCSVResult {
  total_rows: number;
  total_success: number;
  total_fail: number;
  warnings: string[];
}

export interface ComponentValidationError {
  type: 'missing' | 'both_selected' | 'invalid_component' | 'invalid_major_component';
  message: string;
}

export interface ComponentSelectionState {
  component_name?: string;
  major_component_name?: string;
  validation_error?: ComponentValidationError;
}

export function validateComponentSelection(
  component_name?: string,
  major_component_name?: string
): ComponentValidationError | null {
  const _hasComponent = component_name && component_name.trim() !== '';
  const _hasMajorComponent = major_component_name && major_component_name.trim() !== '';

  // Major component is now required
  if (!_hasMajorComponent) {
    return {
      type: 'missing',
      message: 'A major component must be selected'
    };
  }

  // Sub-component (specific component) is optional but can only be selected with a major component
  // This is already satisfied since we require major component above

  return null;
}

export function validateUniqueRow(
  previouslyProcessed?: CSVMappingRow[],
  row?: CSVMappingRow
): boolean {

  if (previouslyProcessed.length == 0) {
    return true;
  }

  for (const prevRow of previouslyProcessed) {
    // Least likely to be duplicate at top
    if (row.major_component_name !== prevRow.major_component_name) {
      continue;
    }
    if (row.component_name !== prevRow.component_name) {
      continue;
    }
    // More likely to be duplicate values at bottom
    if (row.workspace_name !== prevRow.workspace_name) {
      continue;
    }
    if (row.turbine_name !== prevRow.turbine_name) {
      continue;
    }
    if (row.site_name !== prevRow.site_name) {
      continue;
    }
    // must match all values above to get here, is a duplicate row
    return false;
  }
  // row is unique
  return true;
}
